import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import { cx } from "../../utils";
import Button from "./Button";

// A promise-based replacement for window.confirm — call `await confirm({...})`
// and branch on the boolean it resolves to. Rendered as a Tailwind dialog by
// the single <ConfirmProvider> mounted at the app root.
const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
};

export const ConfirmProvider = ({ children }) => {
  const [opts, setOpts] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts(options);
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result) => {
    resolver.current?.(result);
    resolver.current = null;
    setOpts(null);
  }, []);

  // Esc cancels, Enter confirms while the dialog is open.
  useEffect(() => {
    if (!opts) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") settle(false);
      else if (e.key === "Enter") settle(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opts, settle]);

  const tone = opts?.tone ?? "danger";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm anim-in"
            onClick={() => settle(false)}
          >
            <div
              role="alertdialog"
              aria-modal="true"
              className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 pb-5 pt-6">
                <div className="flex items-start gap-3">
                  <span
                    className={cx(
                      "grid h-10 w-10 shrink-0 place-items-center rounded-xl",
                      tone === "danger"
                        ? "bg-rose-50 text-rose-600"
                        : "bg-brand-50 text-brand-600",
                    )}
                  >
                    <AlertTriangle size={18} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold tracking-tight text-ink-900">
                      {opts.title || "Are you sure?"}
                    </h2>
                    {opts.message && (
                      <p className="mt-1 text-sm leading-relaxed text-ink-500">{opts.message}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-6 py-4">
                <Button variant="secondary" onClick={() => settle(false)}>
                  {opts.cancelLabel || "Cancel"}
                </Button>
                <Button
                  variant={tone === "danger" ? "danger" : "gradient"}
                  onClick={() => settle(true)}
                >
                  {opts.confirmLabel || "Confirm"}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
